import { useLocation, useNavigate } from 'react-router-dom'
import EmptyState from '@/components/shared/EmptyState'
import { formatCurrency, formatDate } from '@/lib/utils'
import { IconTarget, IconCalendar } from '@/components/icons/Icons'
import type { Proyecto } from '@/lib/types'

interface TabProyectosProps {
  proyectos: Proyecto[]
  empresaId: number
}

const estadoLabel: Record<string, string> = {
  activo: 'Activo',
  pausado: 'Pausado',
  completado: 'Completado',
  cancelado: 'Cancelado',
}

export default function TabProyectos({ proyectos, empresaId }: TabProyectosProps) {
  const navigate = useNavigate()
  // La ficha del proyecto vive en otra ruta segun el espacio que la monte.
  const enPartner = useLocation().pathname.startsWith('/partner')
  const rutaProyecto = (id: number) => (enPartner ? `/partner/proyectos/${id}` : `/proyectos/${id}`)

  if (proyectos.length === 0) {
    return (
      <EmptyState
        icon={<IconTarget size={40} />}
        title="Sin proyectos"
        description="Esta empresa aun no tiene proyectos registrados."
        actionLabel={enPartner ? undefined : 'Nuevo Proyecto'}
        onAction={enPartner ? undefined : () => navigate(`/proyectos/nuevo?cliente_id=${empresaId}`)}
      />
    )
  }

  return (
    <div className="empresa-proyectos-grid">
      {proyectos.map(p => (
        <div
          key={p.id}
          className="empresa-proyecto-card"
          onClick={() => navigate(rutaProyecto(p.id))}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(rutaProyecto(p.id)) }}
        >
          <div className="empresa-proyecto-card__header">
            <h4 className="empresa-proyecto-card__name">{p.nombre}</h4>
            <span className={`badge badge-${p.estado}`}>
              {estadoLabel[p.estado] || p.estado}
            </span>
          </div>

          {p.descripcion && (
            <p className="empresa-proyecto-card__desc">{p.descripcion}</p>
          )}

          <div className="empresa-proyecto-card__meta">
            <div className="empresa-proyecto-card__meta-item">
              <IconCalendar size={13} />
              <span>
                {p.fecha_inicio ? formatDate(p.fecha_inicio) : '---'}
                {p.fecha_fin_estimada ? ` - ${formatDate(p.fecha_fin_estimada)}` : ''}
              </span>
            </div>
            {p.presupuesto > 0 && (
              <div className="empresa-proyecto-card__budget">
                {formatCurrency(p.presupuesto, p.moneda)}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
